// src/hooks/useAccounting.js
import { useAppContext } from '../components/context/AppContext';

/**
 * Global state hook for accounting operations and periods management
 * Connects to AppContext for centralized state management
 */
export const useAccounting = () => {
    const { state, actions } = useAppContext();

    // Global state from AppContext
    const operations = state.operations || [];
    const periods = state.periods || [];
    const isLoading = state.loading;
    const error = state.error;

    return {
        // Operations state
        operations,
        operationsCount: operations.length,
        
        // Periods state  
        periods: periods.filter(p => p.status !== "archived"), // Filter out archived periods for backward compatibility
        periodsCount: periods.length,
        
        // Loading and error states
        loading: isLoading,
        isLoading,
        error,
        
        // Operations actions
        fetchOperations: actions.fetchOperations,
        createOperation: actions.createOperation,
        updateOperation: actions.updateOperation,
        deleteOperation: actions.deleteOperation,
        autoCalculateIncome: actions.autoCalculateIncome,
        
        // Periods actions
        createPeriod: actions.createPeriod,
        updatePeriod: actions.updatePeriod,
        archivePeriod: actions.archivePeriod,
        
        // Utility functions (preserved from original implementation)
        getOperationsByPeriod: (periodId) => {
            return operations.filter(operation => operation.periodId === periodId);
        },
        
        getTotalIncomeByPeriod: (periodId) => {
            const periodOperations = operations.filter(op => 
                op.periodId === periodId && op.type === 'income'
            );
            return periodOperations.reduce((total, op) => total + (op.amount || 0), 0);
        },
        
        getTotalExpensesByPeriod: (periodId) => {
            const periodOperations = operations.filter(op => 
                op.periodId === periodId && op.type === 'expense'
            );
            return periodOperations.reduce((total, op) => total + (op.amount || 0), 0);
        },
        
        getNetBalanceByPeriod: (periodId) => {
            const income = operations.filter(op => 
                op.periodId === periodId && op.type === 'income'
            ).reduce((total, op) => total + (op.amount || 0), 0);
            
            const expenses = operations.filter(op => 
                op.periodId === periodId && op.type === 'expense'
            ).reduce((total, op) => total + (op.amount || 0), 0);
            
            return income - expenses;
        },
        
        getActivePeriods: () => {
            return periods.filter(period => period.status === 'open');
        },
        
        getArchivedPeriods: () => {
            return state.periods.filter(period => period.status === 'archived');
        },
        
        getCurrentPeriod: () => {
            const currentDate = new Date();
            return state.periods.find(period => {
                const start = new Date(period.startDate);
                const end = new Date(period.endDate);
                return currentDate >= start && currentDate <= end && period.status === 'open';
            });
        }
    };
};